import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Logo } from "@/components/SiteChrome";
import { Avatar } from "@/components/Avatar";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const TONE = {
  VERIFIED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  SUBMITTED: "bg-amber-50 text-amber-700 border-amber-200",
  UNDER_REVIEW: "bg-sky-50 text-sky-700 border-sky-200",
  PENDING: "bg-slate-100 text-slate-600 border-slate-200",
  CORRECTION_REQUESTED: "bg-orange-50 text-orange-700 border-orange-200",
  REJECTED: "bg-red-50 text-red-700 border-red-200",
  SUSPENDED: "bg-red-50 text-red-700 border-red-200",
};

export const StatusBadge = ({ status, testId }) => (
  <span
    data-testid={testId || "status-badge"}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
      TONE[status] || TONE.PENDING
    }`}
  >
    {(status || "PENDING").replaceAll("_", " ")}
  </span>
);

export const DashboardLayout = ({ title, subtitle, nav = [], children, badge }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-sand">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Logo />
          <div className="flex items-center gap-3">
            <Avatar name={user?.full_name} src={user?.avatar_url} size="sm" testId="header-avatar" />
            <span className="hidden text-sm text-slate-500 sm:inline" data-testid="header-user-name">
              {user?.full_name}
            </span>
            <NotificationBell />
            <Button size="sm" variant="outline" onClick={() => { logout(); navigate("/"); }} data-testid="dashboard-logout-btn">
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="dashboard-title">
              {title}
            </h1>
            {subtitle && <p className="mt-1.5 text-sm text-slate-600">{subtitle}</p>}
          </div>
          {badge}
        </div>

        {nav.length > 0 && (
          <nav className="mt-7 flex gap-1.5 overflow-x-auto pb-1" data-testid="dashboard-nav">
            {nav.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    active ? "bg-emerald-900 text-white" : "bg-white text-slate-600 hover:bg-emerald-50 hover:text-emerald-800"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}

        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
};

export const Panel = ({ title, description, children, action, testId }) => (
  <section className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8" data-testid={testId}>
    {(title || action) && (
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          {title && <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>}
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

export const StatCard = ({ label, value, hint, testId }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">{label}</p>
    <p className="mt-2 font-display text-2xl font-extrabold text-slate-900">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

export const Placeholder = ({ title, body, testId }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center" data-testid={testId}>
    <p className="font-display font-bold text-slate-800">{title}</p>
    <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">{body}</p>
    <span className="mt-4 inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
      Coming in a later stage
    </span>
  </div>
);
